import { useCallback, useEffect, useRef, useState, type ChangeEvent } from 'react';
import {
  Loader2, Plus, Heart, MessageCircle, Pin, Paperclip, X,
  FileText, Image as ImageIcon, Download, Send,
} from 'lucide-react';
import { apiClient } from '@/lib/api';
import { cn } from '@/lib/utils';
import { aurora, auroraPalette } from '@/components/aurora/tokens';
import { MobileComposerSheet } from '@/components/mobile/messenger';
import {
  getInitials,
  type FeedAttachment,
  type FeedPost,
  type Workgroup,
} from '@/pages/messenger/workgroup-hub/types';

interface Props {
  workgroup: Workgroup;
  currentUserId: string | null;
  canManage: boolean;
}

interface FeedComment {
  id: string;
  author_id: string;
  author_first_name?: string | null;
  author_last_name?: string | null;
  content_text: string;
  created_at: string;
}

function formatBytes(n: number | null | undefined): string {
  if (!n) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function isImageMime(m: string | null | undefined): boolean {
  return !!m && m.startsWith('image/');
}

export default function MobileWorkgroupFeed({ workgroup, currentUserId, canManage }: Props) {
  const [posts, setPosts] = useState<FeedPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [composerOpen, setComposerOpen] = useState(false);
  const [draft, setDraft] = useState('');
  const [attachments, setAttachments] = useState<File[]>([]);
  const [posting, setPosting] = useState(false);
  const [openComments, setOpenComments] = useState<Record<string, FeedComment[]>>({});
  const [commentDraft, setCommentDraft] = useState<Record<string, string>>({});
  const fileInputRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiClient.get<FeedPost[]>(
        `/messenger/feed/posts?target_type=workgroup&target_id=${workgroup.id}&limit=30`,
      );
      const list: FeedPost[] = Array.isArray(res.data) ? [...res.data] : [];
      list.sort((a, b) => Number(b.is_pinned) - Number(a.is_pinned) || (b.created_at > a.created_at ? 1 : -1));
      setPosts(list);
    } finally {
      setLoading(false);
    }
  }, [workgroup.id]);

  useEffect(() => { load(); }, [load]);

  function resetComposer() {
    setDraft('');
    setAttachments([]);
  }

  async function handlePost() {
    const hasText = draft.trim().length > 0;
    const hasFiles = attachments.length > 0;
    if (!hasText && !hasFiles) return;
    setPosting(true);
    try {
      const fd = new FormData();
      fd.append('post_type', 'message');
      fd.append('target_type', 'workgroup');
      fd.append('target_id', workgroup.id);
      if (hasText) fd.append('content_html', `<p>${draft.replace(/\n/g, '<br>')}</p>`);
      for (const f of attachments) fd.append('files', f);
      await apiClient.post('/messenger/feed/posts', fd);
      resetComposer();
      setComposerOpen(false);
      load();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Errore';
      alert(`Errore creazione post: ${msg}`);
    } finally {
      setPosting(false);
    }
  }

  async function handleReact(post: FeedPost) {
    const liked = post.my_reaction === '❤';
    try {
      if (liked) {
        await apiClient.delete(`/messenger/feed/posts/${post.id}/reactions/${encodeURIComponent('❤')}`);
      } else {
        await apiClient.post(`/messenger/feed/posts/${post.id}/reactions`, { emoji: '❤' });
      }
      load();
    } catch { /* noop */ }
  }

  async function togglePin(post: FeedPost) {
    if (post.is_pinned) await apiClient.delete(`/messenger/feed/posts/${post.id}/pin`);
    else await apiClient.post(`/messenger/feed/posts/${post.id}/pin`, {});
    load();
  }

  async function loadComments(postId: string) {
    if (openComments[postId]) {
      const next = { ...openComments };
      delete next[postId];
      setOpenComments(next);
      return;
    }
    const res = await apiClient.get<FeedComment[]>(`/messenger/feed/posts/${postId}/comments`);
    setOpenComments({ ...openComments, [postId]: Array.isArray(res.data) ? res.data : [] });
  }

  async function postComment(postId: string) {
    const text = commentDraft[postId]?.trim();
    if (!text) return;
    await apiClient.post(`/messenger/feed/posts/${postId}/comments`, {
      content_html: `<p>${text.replace(/\n/g, '<br>')}</p>`,
    });
    setCommentDraft({ ...commentDraft, [postId]: '' });
    const res = await apiClient.get<FeedComment[]>(`/messenger/feed/posts/${postId}/comments`);
    setOpenComments({ ...openComments, [postId]: Array.isArray(res.data) ? res.data : [] });
    load();
  }

  function onFilesPicked(ev: ChangeEvent<HTMLInputElement>) {
    const files = ev.target.files ? Array.from(ev.target.files) : [];
    if (files.length === 0) return;
    setAttachments(prev => [...prev, ...files].slice(0, 10));
    ev.target.value = '';
  }

  function renderAttachment(att: FeedAttachment) {
    const href = `/api/messenger/feed/attachments/${att.id}/download`;
    if (isImageMime(att.mime_type)) {
      return (
        <a key={att.id} href={href} target="_blank" rel="noreferrer" className="block rounded-lg overflow-hidden border border-border bg-muted">
          <img src={href} alt={att.filename} className="max-h-56 object-contain w-full" />
        </a>
      );
    }
    return (
      <a key={att.id} href={href} target="_blank" rel="noreferrer"
         className="flex items-center gap-2 px-3 py-2 bg-muted border border-border rounded-lg text-sm">
        <FileText className="w-4 h-4 text-muted-foreground shrink-0" />
        <span className="flex-1 truncate text-foreground">{att.filename}</span>
        <span className="text-xs text-muted-foreground">{formatBytes(att.file_size)}</span>
        <Download className="w-3.5 h-3.5 text-muted-foreground" />
      </a>
    );
  }

  return (
    <div className="relative h-full">
      <div className="px-3 py-3 space-y-3 pb-24">
        {loading && (
          <div className="text-center text-muted-foreground py-8">
            <Loader2 className="animate-spin inline" />
          </div>
        )}
        {!loading && posts.length === 0 && (
          <div className="text-center py-12 text-muted-foreground/80 text-sm">
            Nessun post nel feed. Tocca il pulsante <Plus className="inline w-3 h-3 mb-0.5" /> per iniziare.
          </div>
        )}
        {posts.map(post => {
          const isAuthor = post.author_id === currentUserId;
          const comments = openComments[post.id];
          const liked = post.my_reaction === '❤';
          return (
            <article key={post.id} className="bg-card border border-border rounded-2xl p-3">
              {post.is_pinned && (
                <div className="flex items-center gap-1.5 text-xs text-amber-400 mb-2">
                  <Pin className="w-3 h-3" /> Post pinnato
                </div>
              )}
              <div className="flex items-start gap-2.5">
                <div className="w-9 h-9 rounded-full bg-muted flex items-center justify-center text-foreground text-xs shrink-0 overflow-hidden">
                  {post.author_avatar_url ? (
                    <img src={post.author_avatar_url} alt="" className="w-full h-full object-cover" />
                  ) : (
                    getInitials(`${post.author_first_name ?? ''} ${post.author_last_name ?? ''}`)
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 text-xs">
                    <span className={cn(aurora.type.bodyStrong, 'text-foreground truncate')}>
                      {post.author_first_name} {post.author_last_name}
                    </span>
                    <span className="text-muted-foreground/80 shrink-0">
                      {new Date(post.created_at).toLocaleString('it-IT', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </div>
                  {post.title && <h3 className="font-semibold text-foreground mt-1 text-sm">{post.title}</h3>}
                  {post.content_html && (
                    <div
                      className="prose prose-invert prose-sm max-w-none mt-1.5 text-foreground text-sm"
                      dangerouslySetInnerHTML={{ __html: post.content_html }}
                    />
                  )}
                  {post.attachments && post.attachments.length > 0 && (
                    <div className="mt-2 space-y-1.5">
                      {post.attachments.map(renderAttachment)}
                    </div>
                  )}
                  <div className="flex items-center gap-3 mt-2.5 text-xs text-muted-foreground">
                    <button
                      onClick={() => handleReact(post)}
                      className={cn('flex items-center gap-1 min-h-[32px]', liked && 'text-pink-400')}
                      aria-label={liked ? 'Rimuovi like' : 'Aggiungi like'}
                    >
                      <Heart className="w-4 h-4" fill={liked ? 'currentColor' : 'none'} />
                      {post.reactions_count ?? 0}
                    </button>
                    <button
                      onClick={() => loadComments(post.id)}
                      className="flex items-center gap-1 min-h-[32px]"
                      aria-label="Commenti"
                    >
                      <MessageCircle className="w-4 h-4" /> {post.comments_count ?? 0}
                    </button>
                    {canManage && (
                      <button
                        onClick={() => togglePin(post)}
                        className={cn('flex items-center gap-1 min-h-[32px] ml-auto', post.is_pinned && 'text-amber-400')}
                        aria-label={post.is_pinned ? 'Rimuovi pin' : 'Pin post'}
                      >
                        <Pin className="w-4 h-4" />
                      </button>
                    )}
                    {(isAuthor || canManage) && !canManage && <span className="ml-auto" />}
                  </div>
                  {comments && (
                    <div className="mt-2 pl-2 border-l-2 border-border space-y-1.5">
                      {comments.map(c => (
                        <div key={c.id} className="text-xs">
                          <span className="font-medium text-foreground">{c.author_first_name} {c.author_last_name}</span>
                          <p className="text-foreground/80 mt-0.5">{c.content_text}</p>
                        </div>
                      ))}
                      <div className="flex gap-1.5 pt-1">
                        <input
                          value={commentDraft[post.id] ?? ''}
                          onChange={(e) => setCommentDraft({ ...commentDraft, [post.id]: e.target.value })}
                          onKeyDown={(e) => e.key === 'Enter' && postComment(post.id)}
                          placeholder="Commenta..."
                          className="flex-1 bg-muted border border-border rounded-lg px-2 py-1.5 text-xs text-foreground"
                        />
                        <button
                          onClick={() => postComment(post.id)}
                          className="px-2 rounded-lg text-white"
                          style={{ background: auroraPalette.indigo }}
                          aria-label="Invia commento"
                        >
                          <Send className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </article>
          );
        })}
      </div>

      <button
        type="button"
        onClick={() => setComposerOpen(true)}
        className="fixed right-4 bottom-6 z-20 w-14 h-14 rounded-full text-white shadow-xl inline-flex items-center justify-center"
        style={{ background: `linear-gradient(135deg, ${auroraPalette.indigo}, ${auroraPalette.violet})`, bottom: 'calc(env(safe-area-inset-bottom) + 1.5rem)' }}
        aria-label="Nuovo post nel feed"
        data-testid="mobile-workgroup-feed-fab"
      >
        <Plus className="w-6 h-6" />
      </button>

      <input ref={fileInputRef} type="file" multiple className="hidden" onChange={onFilesPicked} />

      <MobileComposerSheet
        open={composerOpen}
        onOpenChange={(o) => { if (!o) { resetComposer(); } setComposerOpen(o); }}
        title={`Nuovo post · ${workgroup.name}`}
        placeholder="Scrivi un aggiornamento per il gruppo..."
        value={draft}
        onValueChange={setDraft}
        onSubmit={handlePost}
        submitting={posting}
        submitDisabled={!draft.trim() && attachments.length === 0}
        onPickAttachment={() => fileInputRef.current?.click()}
        submitLabel="Pubblica"
        attachments={attachments.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {attachments.map((f, i) => (
              <span key={i} className="inline-flex items-center gap-1 px-2 py-1 bg-muted rounded text-xs text-foreground/80">
                {f.type.startsWith('image/') ? <ImageIcon className="w-3 h-3" /> : <FileText className="w-3 h-3" />}
                <span className="max-w-[120px] truncate">{f.name}</span>
                <button
                  type="button"
                  onClick={() => setAttachments(prev => prev.filter((_, idx) => idx !== i))}
                  aria-label={`Rimuovi ${f.name}`}
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
          </div>
        )}
        toolbar={(
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="text-xs text-muted-foreground inline-flex items-center gap-1 px-2 py-1"
          >
            <Paperclip className="w-3.5 h-3.5" /> Allegati ({attachments.length})
          </button>
        )}
      />
    </div>
  );
}
